import React, { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { FileText, Upload, Download, Trash2, Paperclip } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function LeadDocuments({ leadId }) {
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [label, setLabel] = useState("Current Policy");
  const fileRef = useRef(null);

  const load = () => api.get(`/leads/${leadId}/documents`).then((r) => setDocs(r.data)).catch(() => {});
  useEffect(() => { load(); }, [leadId]);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("label", label);
      await api.post(`/leads/${leadId}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const download = (id) => {
    const token = localStorage.getItem("ks_token");
    window.open(`${API}/files/${id}/download?auth=${token}`, "_blank", "noopener");
  };
  const remove = async (id) => { await api.delete(`/documents/${id}`); toast.success("Removed"); load(); };
  const fmtSize = (b) => (b > 1024 * 1024 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

  return (
    <div className="bg-white border border-slate-200 rounded-sm p-5" data-testid="documents-panel">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-display text-lg font-bold flex items-center gap-2"><Paperclip size={18} className="text-[#C5A059]" /> Documents</h3>
      </div>
      <div className="flex flex-wrap gap-2 mb-3">
        <select value={label} onChange={(e) => setLabel(e.target.value)} className="h-10 px-3 rounded-sm border border-slate-300 text-sm" data-testid="doc-label">
          {["Current Policy", "Declarations Page", "Driver's License Info", "Vehicle Info", "Quote", "Other"].map((l) => <option key={l}>{l}</option>)}
        </select>
        <input ref={fileRef} type="file" onChange={onPick} className="hidden" accept=".pdf,.png,.jpg,.jpeg,.webp,.heic,.doc,.docx,.csv,.txt" data-testid="doc-file-input" />
        <button onClick={() => fileRef.current?.click()} disabled={uploading} className="bg-[#0B1E36] text-white font-semibold px-4 h-10 rounded-sm flex items-center gap-2 text-sm disabled:opacity-60" data-testid="doc-upload-btn">
          <Upload size={15} /> {uploading ? "Uploading…" : "Upload"}
        </button>
      </div>
      <div className="space-y-2">
        {docs.length === 0 ? <p className="text-sm text-slate-400">No documents yet.</p> :
          docs.map((d) => (
            <div key={d.id} className="flex items-center justify-between border border-slate-100 rounded-sm px-3 py-2" data-testid={`doc-${d.id}`}>
              <div className="flex items-center gap-2 min-w-0">
                <FileText size={16} className="text-slate-400 shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{d.original_filename}</p>
                  <p className="text-xs text-slate-400">{d.label} · {fmtSize(d.size)} · {d.uploaded_by}</p>
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button onClick={() => download(d.id)} className="p-2 hover:text-[#C5A059]" data-testid={`doc-download-${d.id}`}><Download size={16} /></button>
                <button onClick={() => remove(d.id)} className="p-2 hover:text-red-500" data-testid={`doc-delete-${d.id}`}><Trash2 size={16} /></button>
              </div>
            </div>
          ))}
      </div>
    </div>
  );
}
